import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import BrandMark from '../common/BrandMark';
import { useCart } from '../../context/CartContext';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { setIsCartOpen, cart } = useCart();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  const links = [
    { name: 'Shop Goods', path: '/shop' },
    { name: 'Carry Builder', path: '/builder' },
    { name: 'Gift Guide', path: '/gift-guide' },
    { name: 'Workshop Support', path: '/support' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <div className="bg-navy text-cream py-2 text-center text-[9px] font-sans uppercase tracking-[0.3em] border-b border-white/10 px-4">
        Free Workshop Shipping on all domestic orders over $150
      </div>
      
      <nav className={`transition-all duration-500 ${scrolled ? 'bg-cream/95 backdrop-blur-md shadow-lg py-3' : 'bg-cream py-6'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-12">
            
            <div className="flex items-center md:hidden">
              <button 
                onClick={() => setIsOpen(!isOpen)} 
                className="text-navy p-2 hover:text-saddle transition-colors"
                aria-label="Toggle Menu"
              >
                <SafeIcon icon={isOpen ? FiIcons.FiX : FiIcons.FiMenu} className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-shrink-0 flex items-center justify-center md:justify-start flex-1 md:flex-none">
              <Link to="/" className="flex items-center gap-3 group focus:outline-none">
                <BrandMark className="w-9 h-9 text-saddle group-hover:text-brass transition-colors duration-500" />
                <div className="flex flex-col">
                  <span className="font-serif text-lg font-bold tracking-tight text-navy leading-none">HARBOR & HIDE</span>
                  <span className="font-sans text-[7px] tracking-[0.4em] uppercase text-brass mt-1">Heritage Workshop</span>
                </div>
              </Link>
            </div>

            <div className="hidden md:flex flex-1 justify-center items-center space-x-8">
              {links.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`font-sans text-[10px] tracking-[0.2em] uppercase transition-all duration-300 hover:text-saddle relative py-1 group focus:outline-none focus:text-saddle ${
                    isActive(link.path) ? 'text-saddle font-bold' : 'text-navy'
                  }`}
                >
                  {link.name}
                  <span className={`absolute bottom-0 left-0 w-full h-px bg-brass transition-transform duration-500 origin-left ${isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}></span>
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-1 sm:gap-3">
              <button className="hidden sm:flex text-navy hover:text-saddle transition-colors p-2" aria-label="Search">
                <SafeIcon icon={FiIcons.FiSearch} className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setIsCartOpen(true)}
                className="text-navy hover:text-saddle transition-colors p-2 relative group focus:outline-none" 
                aria-label={`Open Cart (${totalItems} items)`}
              >
                <SafeIcon icon={FiIcons.FiShoppingBag} className="w-5 h-5 group-active:scale-90 transition-transform" />
                {totalItems > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-saddle text-cream text-[8px] font-bold flex items-center justify-center rounded-full border border-cream animate-in zoom-in duration-300">
                    {totalItems}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {isOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-cream border-t border-brass/10 shadow-2xl animate-in slide-in-from-top-2 duration-300">
            <div className="px-6 py-10 space-y-2">
              {links.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className="block py-4 font-sans text-xs tracking-[0.2em] uppercase text-navy border-b border-brass/5 active:bg-creamDark transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </div>
  );
}